import {
    View,
    Text,
    StyleSheet,
    ScrollView, Platform,
} from 'react-native';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useOrganizationData} from "@/data/app-data-hooks";
import {ThemeType} from "@/theming/baseTheme";
import {useTranslation} from "react-i18next";
import {useOrganizationNavigation} from "@/hooks/useOrganizationNavigation";
import {HeaderText} from "@/components/common/HeaderText";
import {CreditCard, Calendar, CheckCircle, XCircle, Info} from "lucide-react-native";
import {LogoutButton} from "@/components/user/LogoutButton";
import {MembershipDashboardLink} from "@/components/membership/MembershipDashboardLink";
import {LanguagePicker} from "@/components/user/LanguagePicker";
import {NotificationSettingsCard} from "@/components/notifications/NotificationSettingsCard";

interface MembershipPlanScreenProps {
    orgId: string;
}

export const MembershipPlanScreen = ({ orgId }: MembershipPlanScreenProps) => {
    const {theme} = useCurrentTheme();
    const {t} = useTranslation();
    const styles = createStyles(theme);
    const {data: organizationData} = useOrganizationData(orgId);
    const {navigateBack} = useOrganizationNavigation(orgId);

    if (!organizationData) {
        return (
            <View style={styles.container}>
                <HeaderText
                    title={t('membership.title')}
                    onBackPress={navigateBack}
                />
                <View style={styles.loadingContainer}>
                    <Text style={styles.loadingText}>{t('common.loading')}</Text>
                </View>
            </View>
        );
    }

    const { organization, membership } = organizationData;

    const formatPrice = (price: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: (currency || 'EUR').toUpperCase(),
        }).format(price);
    };

    const isFree = membership.price === 0 || !membership.currency;
    const isActive = !membership.cancelAt || new Date(membership.cancelAt) > new Date();

    return (
        <View style={styles.container}>
            <HeaderText
                title={t('membership.title')}
                onBackPress={navigateBack}
            />

            <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
                {/* Current Plan Card */}
                <View style={styles.planCard}>
                    <View style={styles.planHeader}>
                        <Text style={styles.planName}>{membership.name}</Text>
                        <View style={[styles.statusBadge, isActive ? styles.statusActive : styles.statusInactive]}>
                            {isActive ? (
                                <CheckCircle size={16} color="#fff" />
                            ) : (
                                <XCircle size={16} color="#fff" />
                            )}
                            <Text style={styles.statusText}>
                                {isActive ? t('membership.status.active') : t('membership.status.inactive')}
                            </Text>
                        </View>
                    </View>

                    <View style={styles.priceContainer}>
                        <Text style={styles.priceAmount}>
                            {isFree ? t('offers.free') : formatPrice(membership.price, membership.currency)}
                        </Text>
                        {!isFree && (
                            <Text style={styles.pricePeriod}>{t(`membership.pricing.per.${membership.period}`)}</Text>
                        )}
                    </View>
                </View>

                {/* Plan Details */}
                <View style={styles.detailsCard}>
                    <Text style={styles.sectionTitle}>{t('membership.sections.planDetails')}</Text>

                    <View style={styles.detailItem}>
                        <View style={styles.detailIcon}>
                            <CreditCard size={20} color={theme.colors.textSecondary} />
                        </View>
                        <View style={styles.detailContent}>
                            <Text style={styles.detailLabel}>{t('membership.labels.billingPeriod')}</Text>
                            <Text style={styles.detailValue}>{t(`membership.period.${membership.period}`)}</Text>
                        </View>
                    </View>

                    <View style={styles.detailItem}>
                        <View style={styles.detailIcon}>
                            <Calendar size={20} color={theme.colors.textSecondary} />
                        </View>
                        <View style={styles.detailContent}>
                            <Text style={styles.detailLabel}>{t('membership.labels.memberSince')}</Text>
                            <Text style={styles.detailValue}>
                                {t('common:date', {
                                    value: new Date(membership.memberSince),
                                    formatParams: {
                                        value: {
                                            dateStyle: 'medium',
                                        }
                                    }
                                })}
                            </Text>
                        </View>
                    </View>

                    {membership.cancelAt && (
                        <View style={styles.detailItem}>
                            <View style={styles.detailIcon}>
                                <Calendar size={20} color={theme.colors.error} />
                            </View>
                            <View style={styles.detailContent}>
                                <Text style={styles.detailLabel}>{t('membership.labels.validUntil')}</Text>
                                <Text style={[styles.detailValue, styles.detailValueError]}>
                                    {t('common:date', {
                                        value: new Date(membership.cancelAt),
                                        formatParams: {
                                            value: {
                                                dateStyle: 'medium',
                                            }
                                        }
                                    })}
                                </Text>
                                <Text style={[styles.detailValue, styles.detailValueError]}>
                                    {t('membership.labels.cancelled')}
                                </Text>
                            </View>
                        </View>
                    )}
                </View>

                {/* Organization Info */}
                <View style={styles.orgCard}>
                    <Text style={styles.sectionTitle}>{t('membership.sections.organization')}</Text>
                    <Text style={styles.orgName}>{organization.name}</Text>
                    <Text style={styles.orgDescription}>
                        {t('membership.organization.memberDescription')}
                    </Text>
                </View>

                <MembershipDashboardLink organization={organization} />

                <LanguagePicker />

                <NotificationSettingsCard />

                {/* Logout Section */}
                <View style={styles.logoutSection}>
                    <LogoutButton />
                </View>

                <View style={styles.bottomSpacing} />
            </ScrollView>
        </View>
    );
};

const createStyles = (theme: ThemeType) =>
    StyleSheet.create({
        container: {
            flex: 1,
            backgroundColor: theme.colors.background,
        },
        scrollView: {
            flex: 1,
        },
        loadingContainer: {
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
        },
        loadingText: {
            color: theme.colors.text,
            fontSize: theme.fontSize.md,
        },
        planCard: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.primary,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            shadowColor: theme.colors.primary,
            shadowOffset: {
                width: 0,
                height: 4,
            },
            shadowOpacity: 0.2,
            shadowRadius: 8,
            elevation: 5,
        },
        planHeader: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing.md,
        },
        planName: {
            fontSize: theme.fontSize.xl,
            fontWeight: '700',
            color: '#FFFFFF',
        },
        statusBadge: {
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: theme.spacing.xs,
            paddingHorizontal: theme.spacing.sm,
            borderRadius: theme.borderRadius.full,
            gap: theme.spacing.xs,
        },
        statusActive: {
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
        },
        statusInactive: {
            backgroundColor: theme.colors.error,
        },
        statusText: {
            color: '#FFFFFF',
            fontSize: theme.fontSize.sm,
            fontWeight: '600',
        },
        priceContainer: {
            alignItems: 'center',
        },
        priceAmount: {
            fontSize: 36,
            fontWeight: '700',
            color: '#FFFFFF',
        },
        pricePeriod: {
            fontSize: theme.fontSize.md,
            color: 'rgba(255, 255, 255, 0.8)',
        },
        detailsCard: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        sectionTitle: {
            fontSize: theme.fontSize.lg,
            fontWeight: '700',
            color: theme.colors.text,
            marginBottom: theme.spacing.lg,
        },
        detailItem: {
            flexDirection: 'row',
            alignItems: 'center',
            marginBottom: theme.spacing.lg,
        },
        detailIcon: {
            width: 40,
            height: 40,
            borderRadius: theme.borderRadius.md,
            backgroundColor: theme.colors.background,
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: theme.spacing.md,
        },
        detailContent: {
            flex: 1,
        },
        detailLabel: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            marginBottom: 2,
        },
        detailValue: {
            fontSize: theme.fontSize.md,
            fontWeight: '600',
            color: theme.colors.text,
        },
        detailValueError: {
            color: theme.colors.error,
        },
        orgCard: {
            margin: theme.spacing.md,
            backgroundColor: theme.colors.surface,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            borderWidth: 1,
            borderColor: theme.colors.border,
        },
        orgName: {
            fontSize: theme.fontSize.md,
            fontWeight: '600',
            color: theme.colors.text,
            marginBottom: theme.spacing.xs,
        },
        orgDescription: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
        },
        infoCard: {
            margin: theme.spacing.md,
            backgroundColor: `${theme.colors.primary}10`,
            borderRadius: theme.borderRadius.lg,
            padding: theme.spacing.lg,
            flexDirection: 'row',
            borderWidth: 1,
            borderColor: `${theme.colors.primary}20`,
        },
        infoIcon: {
            marginRight: theme.spacing.md,
            marginTop: 2,
        },
        infoContent: {
            flex: 1,
        },
        infoTitle: {
            fontSize: theme.fontSize.md,
            fontWeight: '600',
            color: theme.colors.text,
            marginBottom: theme.spacing.xs,
        },
        infoText: {
            fontSize: theme.fontSize.sm,
            color: theme.colors.textSecondary,
            lineHeight: theme.fontSize.sm * 1.5,
        },
        logoutSection: {
            marginTop: theme.spacing.lg,
            marginHorizontal: theme.spacing.md,
            paddingTop: theme.spacing.lg,
            borderTopWidth: 1,
            borderTopColor: theme.colors.border,
        },
        bottomSpacing: {
            height: theme.spacing.xl,
        },
    });
